import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import detectBrowserLanguage from "detect-browser-language";

import useGetPlanPrices from "../../../../hooks/useGetPlanPrices.js";

import { setNavigationIndex } from "../../../../redux/reducers/uiReducer.js";

import { errorToast } from "../../../toast";
import ProPlanCard from "./pro-plan-card/index.jsx";
import FreePlanCard from "./free-plan-card/index.jsx";
import UnregPlanCard from "./unreg-plan-card/index.jsx";

import {
  isEmpty,
  isEmptyList,
  isEmptyObject,
  equalsIgnoringCase,
} from "../../../../utils/validators.js";
import { logDebug, logError } from "../../../../utils/logger.js";

function PlansSection() {
  const { t, i18n } = useTranslation();

  const containerRef = useRef(null);

  const [planPrices, setPlanPrices] = useState([]);

  const matchT = useMatch("get/t/:id");
  const matchTF = useMatch("get/tf/:id");

  const dispatch = useDispatch();
  const { ui } = useSelector((state) => state);

  const {
    data: dataGetPlanPrices,
    error: errorGetPlanPrices,
    loading: loadingGetPlanPrices,
    getPlanPrices,
    clear: clearGetPlanPrices,
  } = useGetPlanPrices();

  const loading = useMemo(() => {
    return [loadingGetPlanPrices].some((i) => i);
  }, [loadingGetPlanPrices]);

  const source = useMemo(() => {
    return [matchT, matchTF].some((i) => i) ? "plans_d" : "plans_u";
  }, [matchT, matchTF]);

  const unregPlan = useMemo(() => {
    return {
      id: "UNREG",
      metadata: [],
      type: "",
      currency: "eur",
      unit_amount_decimal: "0",
    };
  }, []);

  const freePlan = useMemo(() => {
    return {
      id: "FREE",
      metadata: [],
      type: "",
      currency: "eur",
      unit_amount_decimal: "0",
    };
  }, []);

  const locale = useMemo(() => {
    const userLocale = detectBrowserLanguage();
    let locale = userLocale.split("-")[0];
    if (!isEmpty(locale)) {
      if (!i18n.languages.includes(locale)) {
        locale = "en";
      }
    } else {
      locale = "en";
    }
    return locale;
  }, [i18n.languages]);

  const proPlan = useMemo(() => {
    if (!isEmptyList(planPrices)) {
      const monthlyPlanId = process.env.REACT_APP_STRIPE_MONTH_PRICE_ID;
      return planPrices.find((i) => equalsIgnoringCase(monthlyPlanId, i.id));
    }
    return {
      id: "PRO",
      metadata: [],
      type: "recurring",
      currency: "eur",
      unit_amount_decimal: "1200",
    };
  }, [planPrices]);

  const onClickRegisterFreeHandler = useCallback(
    (evt) => {
      logDebug("PlansSection", "onClickRegisterFreeHandler", {
        evt: evt,
        source: source,
        subscription: t("subscription.free"),
      });
      let domain = "";
      if (equalsIgnoringCase(process.env.REACT_APP_ENV, "PREPRODUCTION")) {
        domain = "pre.ydray.com";
      } else {
        domain = process.env.REACT_APP_DOMAIN;
      }
      const path = t("routes.public.register");
      window.location.href = `https://${domain}/${locale}/${path}/`;
    },
    [t, locale, source]
  );

  const onClickRegisterProHandler = useCallback(
    (evt) => {
      logDebug("PlansSection", "onClickRegisterProHandler", {
        evt: evt,
        source: source,
        subscription: t("subscription.pro"),
      });
      let domain = "";
      if (equalsIgnoringCase(process.env.REACT_APP_ENV, "PREPRODUCTION")) {
        domain = "pre.ydray.com";
      } else {
        domain = process.env.REACT_APP_DOMAIN;
      }
      const path = t("routes.public.register");
      window.location.href = `https://${domain}/${locale}/${path}/`;
    },
    [t, locale, source]
  );

  useEffect(() => {
    if (!loadingGetPlanPrices) {
      if (!isEmpty(errorGetPlanPrices)) {
        logError("PlansSection", "useGetPlanPrices", {
          error: errorGetPlanPrices,
          msg: t(`errors.${errorGetPlanPrices}`, t("errors.default")),
        });
        errorToast(
          "PlansSection",
          errorGetPlanPrices,
          t(`errors.${errorGetPlanPrices}`, t("errors.default"))
        );
        setPlanPrices([]);
        clearGetPlanPrices();
      } else if (!isEmptyObject(dataGetPlanPrices)) {
        logDebug("PlansSection", "useGetPlanPrices", {
          data: dataGetPlanPrices,
          msg: t(`message.success`, t("message.default")),
        });
        setPlanPrices(dataGetPlanPrices);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGetPlanPrices, errorGetPlanPrices, loadingGetPlanPrices]);

  useEffect(() => {
    const handler = (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting) {
        getPlanPrices({
          productId: process.env.REACT_APP_STRIPE_PRODUCT_ID,
        });
        observer.disconnect();
      }
    };

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handler, options);

    const ref = containerRef.current;
    if (ref) observer.observe(ref);

    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [getPlanPrices]);

  useEffect(() => {
    const handler = (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting) {
        if (!equalsIgnoringCase("landing#plans", ui.navigation_index)) {
          dispatch(setNavigationIndex("landing#plans"));
        } else {
          dispatch(setNavigationIndex(""));
        }
      } else {
        dispatch(setNavigationIndex(""));
      }
    };

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(handler, options);

    const ref = containerRef.current;
    if (ref) observer.observe(ref);

    return () => {
      if (ref) observer.unobserve(ref);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} id="plans" className="plans__section">
      <div className="plans__section__header">
        <div className="plans__section__header__content">
          <div className="plans__section__header__subtitle">
            <span>{t("landing.body.plans_section.subtitle")}</span>
          </div>
          <div className="plans__section__header__title">
            <h2>{t("landing.body.plans_section.title")}</h2>
          </div>
          <div
            className="plans__section__header__description"
            dangerouslySetInnerHTML={{
              __html: t("landing.body.plans_section.description", {
                interpolation: { escapeValue: true },
              }),
            }}
          ></div>
        </div>
      </div>
      <div className="plans__section__body">
        <div className="plans__section__body__content">
          <UnregPlanCard loading={loading} plan={unregPlan} />
          <FreePlanCard
            loading={loading}
            plan={freePlan}
            showRegister={true}
            onRegister={(evt) => onClickRegisterFreeHandler(evt)}
          />
          <ProPlanCard
            loading={loading}
            plan={proPlan}
            showRegister={true}
            onRegister={(evt) => onClickRegisterProHandler(evt)}
          />
        </div>
      </div>
      <div className="plans__section__footer"></div>
    </div>
  );
}

export default PlansSection;
